import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Pressable } from 'react-native';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { palettes } from '../config';
import { getResponsiveStyle } from '../utils/getResponsiveStyle';
import { GoBackButton } from '../components';
import { useLazyQuery, gql } from '@apollo/client';
import { useSetAlert } from '../hooks/useSetAlerts';
import DesktopTemplate from '../components/common/templates/DesktopTemplate';

// Define the registrations query
const GET_EVENT_REGISTRATIONS = gql`
  query getEventRegistrations($eventId: ID!, $page: Int!, $limit: Int!) {
    getEventRegistrations(input: {
      eventId: $eventId,
      page: $page,
      limit: $limit
    }){
      statusCode
      data {
        ...on All {
          data {
            ...on EventAnalytics {
              identifier
              createdAt
            }
          }
        }
      }
    }
  }
`;

type RouteParams = {
    EventRegistrations: {
        eventId: string;
    };
};

type Registration = {
    identifier: string;
    createdAt: string;
};

const EventRegistrations = () => {
    const route = useRoute<RouteProp<RouteParams, 'EventRegistrations'>>();
    const { eventId } = route.params;
    const navigation = useNavigation();
    const { setAlert } = useSetAlert();
    const [loading, setLoading] = useState(true);
    const [registrations, setRegistrations] = useState<Registration[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);

    const [getEventRegistrations, { loading: fetchLoading }] = useLazyQuery(GET_EVENT_REGISTRATIONS, {
        variables: { eventId, page: currentPage, limit },
        onCompleted: (data) => {
            console.log({ data });
            if (data?.getEventRegistrations?.data?.data) {
                const registrationsData = data.getEventRegistrations.data.data;
                setRegistrations(registrationsData);
                setLoading(false);
            } else {
                setAlert('Failed to load registrations data', 'danger');
                setLoading(false);
            }
        },
        onError: (error) => {
            setAlert(error.message, 'danger');
            setLoading(false);
        }
    });

    useEffect(() => {
        getEventRegistrations();
    }, [currentPage]);

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(parseInt(dateString) || dateString);
            
            // Using native JS date formatting
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[date.getMonth()];
            const day = date.getDate().toString().padStart(2, '0');
            const year = date.getFullYear();
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            
            return `${month} ${day}, ${year} ${hours}:${minutes}`;
        } catch (error) {
            return dateString;
        }
    };

    const loadNextPage = () => {
        setCurrentPage(prev => prev + 1);
    };

    const loadPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    return (
        <DesktopTemplate>
            <View style={styles.header}>
                <GoBackButton />
                <Text style={styles.headerTitle}>Event Registrations</Text>
            </View>

            {loading || fetchLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={palettes.primary[0]} />
                    <Text style={styles.loadingText}>Loading registrations data...</Text>
                </View>
            ) : (
                <View style={styles.tableContainer}>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.tableHeaderCell, { flex: 1 }]}>User</Text>
                        <Text style={[styles.tableHeaderCell, { flex: 1 }]}>Registration Date</Text>
                    </View>
                    
                    {registrations.length === 0 ? (
                        <View style={styles.emptyState}>
                            <Text style={styles.emptyStateText}>No registrations found</Text>
                        </View>
                    ) : (
                        registrations.map((registration, index) => (
                            <View key={registration.identifier || index} style={styles.tableRow}>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{registration.identifier}</Text>
                                <Text style={[styles.tableCell, { flex: 1 }]}>{formatDate(registration.createdAt)}</Text>
                            </View>
                        ))
                    )}
                    
                    <View style={styles.pagination}>
                        <Pressable 
                            onPress={loadPreviousPage} 
                            style={[styles.paginationButton, currentPage === 1 && styles.disabledButton]}
                            disabled={currentPage === 1}
                        >
                            <Text style={styles.paginationButtonText}>Previous</Text>
                        </Pressable>
                        <Text style={styles.paginationText}>Page {currentPage}</Text>
                        <Pressable 
                            onPress={loadNextPage} 
                            style={styles.paginationButton}
                        >
                            <Text style={styles.paginationButtonText}>Next</Text>
                        </Pressable>
                    </View>
                </View>
            )}
        </DesktopTemplate>
    );
};

const styles = StyleSheet.create({
    header: {
        padding: getResponsiveStyle(16),
        backgroundColor: palettes.light[0],
        borderBottomWidth: 1,
        borderBottomColor: palettes.grey[5],
        flexDirection: 'row',
        alignItems: 'center',
    },
    headerTitle: {
        fontSize: getResponsiveStyle(18, 'font'),
        fontWeight: '700',
        color: palettes.dark[0],
        marginLeft: getResponsiveStyle(16),
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: getResponsiveStyle(32),
        marginTop: getResponsiveStyle(40),
    },
    loadingText: {
        marginTop: getResponsiveStyle(16),
        fontSize: getResponsiveStyle(14, 'font'),
        color: palettes.grey[1],
    },
    tableContainer: {
        margin: getResponsiveStyle(16),
        backgroundColor: 'white',
        borderRadius: 8,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: palettes.grey[6],
        paddingVertical: getResponsiveStyle(12),
        paddingHorizontal: getResponsiveStyle(16),
    },
    tableHeaderCell: {
        fontSize: getResponsiveStyle(14, 'font'),
        fontWeight: '600',
        color: palettes.grey[1],
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: getResponsiveStyle(12),
        paddingHorizontal: getResponsiveStyle(16),
        borderBottomWidth: 1,
        borderBottomColor: palettes.grey[5],
    },
    tableCell: {
        fontSize: getResponsiveStyle(14, 'font'),
        color: palettes.dark[0],
    },
    emptyState: {
        padding: getResponsiveStyle(32),
        alignItems: 'center',
    },
    emptyStateText: {
        fontSize: getResponsiveStyle(16, 'font'),
        color: palettes.grey[1],
    },
    pagination: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        padding: getResponsiveStyle(16),
    },
    paginationButton: {
        backgroundColor: palettes.primary[0],
        paddingVertical: getResponsiveStyle(8),
        paddingHorizontal: getResponsiveStyle(16),
        borderRadius: 4,
        marginHorizontal: getResponsiveStyle(8),
    },
    paginationButtonText: {
        color: 'white',
        fontSize: getResponsiveStyle(14, 'font'),
        fontWeight: '600',
    },
    disabledButton: {
        backgroundColor: palettes.grey[4],
    },
    paginationText: {
        fontSize: getResponsiveStyle(14, 'font'),
        color: palettes.dark[0],
    },
});

export default EventRegistrations; 